/**
 * Settings: the read-only roles roster.
 *
 * Configuration is changed rarely and never while watching the team work, which
 * is why these are one group rather than four menu entries.
 */

import { useState, type ReactNode } from 'react';

import { formatTimestamp } from '../../logic/format.js';
import {
  useConsoleMutation,
  useIntegrations,
  useSkillContents,
  useTemplates,
} from '../queries.js';
import { Chip, QueryPhase, Section } from '../components/state.js';
import { useStore } from '../store-context.js';
import { Button, Flash } from '@primer/react';

export function RolesScreen(): ReactNode {
  const templates = useTemplates();
  const store = useStore();
  const hasTeam = store.config.teamId !== null;
  const [name, setName] = useState('');
  const [created, setCreated] = useState<{ team: string; version: number } | null>(null);

  /**
   * The whole first run in one action: team row, drafted version, activation.
   *
   * Three API calls because §13.1 makes them three steps — but a person
   * setting up their first team should press one button, and each step's
   * failure surfaces as itself (a compile error names its field; a duplicate
   * slug names the slug).
   */
  const create = useConsoleMutation<{ template: NonNullable<ReturnType<typeof useTemplates>['data']>[number] }, void>(
    async (data, context, { template }) => {
      const slug = slugify(name);
      const document = (template.document ?? '').replace(/^(\s*name:).*$/mu, `$1 ${slug}`);
      const team = await data.createTeam(slug, name.trim(), context);
      const draft = await data.draftTeamVersion(team.id, document, {
        ...context,
        idempotencyKey: `${context.idempotencyKey}-draft`,
      });
      if (!draft.drafted || typeof draft.version !== 'number') {
        throw new Error(
          (draft.errors ?? []).map((error) => `${error.path} — ${error.message}`).join('\n') || 'the document did not compile',
        );
      }
      await data.activateTeamVersion(team.id, draft.version, {
        ...context,
        idempotencyKey: `${context.idempotencyKey}-activate`,
      });
      setCreated({ team: name.trim(), version: draft.version });
    },
    [['team-versions'], ['team-status']],
  );

  return (
    <div className="stack">
      {hasTeam ? null : created !== null ? (
        <Section label="Team created">
          <Flash variant="success">
            <strong>
              «{created.team}» — version {created.version} is live.
            </strong>
            <p>The daemon is running it now. Reload this page and the board, the agents and the operator are there.</p>
          </Flash>
        </Section>
      ) : (
        <Section
          label="Create a team"
          lede="Pick a template, name the team, press create. The document is compiled — every error at once — activated, and running before the button releases."
        >
          <div className="editor-grid">
            <label className="field">
              <span className="field-label">Team name</span>
              <input value={name} placeholder="my-engineering" onChange={(event) => setName(event.target.value)} />
            </label>
          </div>
          {create.isError ? (
            <Flash variant="danger" role="alert">
              <strong>The compiler refused this document</strong>
              <pre className="finding-list">{String(create.error)}</pre>
            </Flash>
          ) : null}
        </Section>
      )}

      <Section label="Starting template" lede="A template fills the draft. Everything in it can be edited afterwards, and nothing in it is platform source (AC-002).">
        <QueryPhase isPending={templates.isPending} error={templates.error} source="team templates" rows={3} />
        {templates.data === undefined ? null : (
          <ul className="template-picker">
            {templates.data.map((template) => (
              <li className="template-option" key={template.id}>
                <div>
                  <strong>{template.name}</strong>
                  <p className="template-summary">{template.summary}</p>
                  <p className="template-meta">
                    {template.boardAdapter} · {template.workflow} · roles: {template.roles.join(', ')}
                  </p>
                </div>
                {hasTeam || created !== null ? null : template.document == null ? (
                  <span className="hint">no document in this install</span>
                ) : (
                  <Button variant="primary"
                    disabled={name.trim().length === 0 || create.isPending}
                    onClick={() => create.mutate({ template })}
                  >
                    {create.isPending ? 'Creating…' : 'Create from this'}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {hasTeam ? (
        <Section label="Editing this team">
          <p className="hint">
            The team is edited under <strong>Roles &amp; team</strong> and <strong>Board columns</strong>; every save
            applies immediately and is kept as a numbered version in the history.
          </p>
        </Section>
      ) : null}
    </div>
  );
}

/** `my Engineering Team` → `my-engineering-team`; the compiler wants an identifier. */
function slugify(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/gu, '-')
      .replace(/^-+|-+$/gu, '') || 'team'
  );
}

export function SkillsScreen(): ReactNode {
  const skills = useSkillContents();
  const integrations = useIntegrations();

  const save = useConsoleMutation<{ ref: string; name: string; text: string }, void>(
    (data, context, args) => data.saveSkill(args.ref, args.name, args.text, context),
    [['skill-contents'], ['skills']],
  );
  const reset = useConsoleMutation<string, void>((data, context, ref) => data.resetSkill(ref, context), [
    ['skill-contents'],
    ['skills'],
  ]);

  return (
    <div className="stack">
      <Section
        label="Skill text"
        lede="What each role is told beyond its own instructions. A skill adds knowledge; it cannot grant a permission."
      >
        <QueryPhase isPending={skills.isPending} error={skills.error} source="skill content" rows={4} />
        {skills.data === undefined
          ? null
          : skills.data.map((skill) => <SkillEditor skill={skill} save={save} reset={reset} key={skill.ref} />)}
      </Section>

      <Section label="Connections">
        <QueryPhase isPending={integrations.isPending} error={integrations.error} source="integrations" rows={2} />
        {integrations.data === undefined ? null : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Integration</th>
                <th>Type</th>
                <th>Health</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {integrations.data.map((integration) => (
                <tr key={integration.id}>
                  <td>{integration.id}</td>
                  <td>{integration.type}</td>
                  <td>
                    <Chip
                      tone={integration.health === 'healthy' ? 'active' : integration.health === 'failing' ? 'danger' : 'unknown'}
                      text={integration.health}
                    />
                  </td>
                  <td>{integration.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function SkillEditor({
  skill,
  save,
  reset,
}: {
  skill: NonNullable<ReturnType<typeof useSkillContents>['data']>[number];
  save: ReturnType<typeof useConsoleMutation<{ ref: string; name: string; text: string }, void>>;
  reset: ReturnType<typeof useConsoleMutation<string, void>>;
}): ReactNode {
  const [text, setText] = useState(skill.text);

  return (
    <div className="skill-editor">
      <div className="skill-editor-head">
        <strong>{skill.name}</strong>
        <Chip tone={skill.source === 'builtin' ? 'neutral' : 'info'} text={skill.source} />
        <code>{skill.digest.slice(0, 12)}…</code>
      </div>
      <textarea value={text} rows={6} onChange={(event) => setText(event.target.value)} />
      <div className="skill-editor-actions">
        <Button variant="primary"
          disabled={text === skill.text || save.isPending}
          onClick={() => save.mutate({ ref: skill.ref, name: skill.name, text })}
        >
          Save
        </Button>
        {skill.source === 'builtin' ? null : (
          <Button onClick={() => reset.mutate(skill.ref)}>
            Reset to built-in
          </Button>
        )}
      </div>
    </div>
  );
}

